import React, { useState, useEffect } from "react";
import { X, Upload, Trash2 } from "lucide-react";

const PaymentModal = ({ isOpen, onClose, onSubmit, existingPaymentData }) => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      name: "Clicktocart",
      amount: "",
      images: [],
      selected: false,
    },
    {
      id: 2,
      name: "Swapnil Gautam",
      amount: "",
      images: [],
      selected: false,
    },
  ]);

  const [errors, setErrors] = useState({});

  // ✅ Load existing payment data when modal opens
  useEffect(() => {
    if (isOpen && existingPaymentData?.paymentMethods?.length > 0) {
      const updatedMethods = paymentMethods.map((method) => {
        const existingMethod = existingPaymentData.paymentMethods.find(
          (pm) => pm.paymentOption === method.name
        );

        if (existingMethod) {
          return {
            ...method,
            selected: true,
            amount: existingMethod.paymentAmount.toString(),
            images: existingMethod.paymentProofImages.map((file) => ({
              file,
              preview: file instanceof File ? URL.createObjectURL(file) : file,
              isExisting: true,
            })),
          };
        }
        return method;
      });

      setPaymentMethods(updatedMethods);
    }
  }, [isOpen, existingPaymentData]);

  const togglePaymentMethod = (id) => {
    setPaymentMethods((prev) =>
      prev.map((method) =>
        method.id === id ? { ...method, selected: !method.selected } : method
      )
    );
    setErrors((prev) => ({ ...prev, methods: null }));
  };

  const removePaymentMethod = (id) => {
    setPaymentMethods((prev) =>
      prev.map((method) => {
        if (method.id === id) {
          // Clean up image previews
          method.images.forEach((img) => {
            if (!img.isExisting && img.preview) {
              URL.revokeObjectURL(img.preview);
            }
          });
          
          return {
            ...method,
            selected: false,
            amount: "",
            images: [],
          };
        }
        return method;
      })
    );
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`amount-${id}`];
      delete newErrors[`images-${id}`];
      return newErrors;
    });
  };

  const handleAmountChange = (id, value) => {
    setPaymentMethods((prev) =>
      prev.map((method) =>
        method.id === id ? { ...method, amount: value } : method
      )
    );
    setErrors((prev) => ({ ...prev, [`amount-${id}`]: null }));
  };

  const handleImageChange = (id, e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    }));

    setPaymentMethods((prev) =>
      prev.map((method) =>
        method.id === id
          ? { ...method, images: [...method.images, ...newImages] }
          : method
      )
    );
    setErrors((prev) => ({ ...prev, [`images-${id}`]: null }));
  };

  const handleRemoveImage = (methodId, imageIndex) => {
    setPaymentMethods((prev) =>
      prev.map((method) => {
        if (method.id === methodId) {
          const imageToRemove = method.images[imageIndex];
          const updatedImages = method.images.filter((_, i) => i !== imageIndex);
          
          // Only revoke URL if it's a newly created preview (not existing)
          if (!imageToRemove.isExisting && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
          }
          
          return { ...method, images: updatedImages };
        }
        return method;
      })
    );
  };

  const calculateTotalAmount = () => {
    return paymentMethods
      .filter((m) => m.selected)
      .reduce((sum, m) => sum + (Number(m.amount)), 0);
  };

  const handleSubmit = () => {
    const newErrors = {};
    const selectedMethods = paymentMethods.filter((m) => m.selected);

    selectedMethods.forEach((method) => {
      if (!method.amount || Number(method.amount) <= 0) {
        newErrors[`amount-${method.id}`] =
          "Please enter a valid amount for this method";
      }
      
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const paymentData = selectedMethods.map((method) => ({
        paymentOption: method.name,
        paymentAmount: Number(method.amount),
        paymentProofImages: method.images.map((img) => img.file),
      }));

      onSubmit({
        paymentMethods: paymentData,
        totalAmount: calculateTotalAmount(),
      });

      handleClose();
    }
  };

  const handleClose = () => {
    paymentMethods.forEach((method) => {
      method.images.forEach((img) => {
        if (!img.isExisting && img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    });

    setPaymentMethods([
      {
        id: 1,
        name: "Clicktocart",
        amount: "",
        images: [],
        selected: false,
      },
      {
        id: 2,
        name: "Swapnil Gautam",
        amount: "",
        images: [],
        selected: false,
      },
    ]);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">
            Payment Details - Multiple Options
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {errors.methods && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.methods}
            </div>
          )}

          {/* Payment Methods */}
          <div className="space-y-6">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`border rounded-lg p-4 transition-all ${
                  method.selected
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-white"
                }`}
              >
                {/* Select Checkbox with Remove Button */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`method-${method.id}`}
                      checked={method.selected}
                      onChange={() => togglePaymentMethod(method.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    />
                    <label
                      htmlFor={`method-${method.id}`}
                      className="ml-2 text-lg font-semibold text-gray-800 cursor-pointer"
                    >
                      {method.name}
                    </label>
                  </div>
                  
                  {method.selected && (
                    <button
                      onClick={() => removePaymentMethod(method.id)}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                      type="button"
                    >
                      <Trash2 size={14} />
                      
                    </button>
                  )}
                </div>

                {method.selected && (
                  <>
                    {/* Payment Amount */}
                    <div className="mb-4">
                      <label className="block mb-2 font-semibold text-gray-700">
                        Payment Amount (₹){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={method.amount}
                        onChange={(e) =>
                          handleAmountChange(method.id, e.target.value)
                        }
                        placeholder="Enter payment amount"
                        className={`w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`amount-${method.id}`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {errors[`amount-${method.id}`] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[`amount-${method.id}`]}
                        </p>
                      )}
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block mb-2 font-semibold text-gray-700">
                        Payment Proof Images{" "}
                        <span className="text-red-500">*</span>
                      </label>

                      {/* Upload Button */}
                      <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors">
                        <div className="text-center">
                          <Upload
                            className="mx-auto mb-2 text-gray-400"
                            size={28}
                          />
                          <p className="text-sm text-gray-600">
                            Click to upload payment proof images
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            PNG, JPG, JPEG (Max 5MB each)
                          </p>
                        </div>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleImageChange(method.id, e)}
                          className="hidden"
                        />
                      </label>

                      {errors[`images-${method.id}`] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[`images-${method.id}`]}
                        </p>
                      )}

                      {/* Image Previews */}
                      {method.images.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {method.images.map((img, index) => (
                            <div
                              key={index}
                              className="relative group border rounded-lg overflow-hidden"
                            >
                              <img
                                src={img.preview}
                                alt={`Payment proof ${index + 1}`}
                                className="w-full h-24 object-cover"
                              />
                              <button
                                onClick={() =>
                                  handleRemoveImage(method.id, index)
                                }
                                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={14} />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 truncate">
                                {img.file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Total Amount Summary */}
          {paymentMethods.some((m) => m.selected) && (
            <div className="mt-6 p-4 bg-green-50 border border-green-300 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-800">
                  Total Payment Amount:
                </span>
                <span className="text-2xl font-bold text-green-600">
                  ₹ {calculateTotalAmount().toFixed(2)}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-700">
                {paymentMethods
                  .filter((m) => m.selected)
                  .map((m) => (
                    <div key={m.id}>
                      {m.name}: ₹{m.amount}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-4 border-t">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            Submit Payment Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;